import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
} from '@angular/core';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk/';
import { ProductDetailConfiguration } from '@types';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { Logger } from '@utils/logger';
import { FundDocument } from '@components/products/types/fund-document.type';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@components/shared/translate/translate.service';
import { OtherFundDetailsComponentConfig } from "@products/other-details/other-fund-details-component.config";
import {
  OtherFundDetailsDisplayComponent
} from "@products/other-details/other-fund-details-display-component.interface";
import { ActivatedRoute } from '@angular/router';

const logger = Logger.getLogger('FundRisksComponent');

@Component({
  selector: 'ft-fund-risks',
  templateUrl: './fund-risks.component.html',
  styleUrls: ['./fund-risks.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundRisksComponent
  extends BaseProductDetailComponent
  implements OtherFundDetailsDisplayComponent {
  productLabel: SafeHtml;
  productLabelLoaded: boolean;
  hasProductLabel = false;

  constructor(
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,private route: ActivatedRoute,
    private sanitizer: DomSanitizer, private elementRef: ElementRef 
  ) {
    super();
    this.route.queryParams.subscribe((queryParams) => {
      const scrollToRisks = queryParams.scrolltorisks;
      if (scrollToRisks) {
        this.scrollToRisks();
      }
      
    })
  }

  private scrollToRisks() {
    let retryTimeout;
    let retries = 0;
    const maxRetries = 5; // Seting a maximum number of retries
    const timeoutLimit = 5000; // Settin a timeout limit in milliseconds
  
    const attemptScroll = () => {
      const element = this.elementRef.nativeElement.querySelector('#fund-riskometer');
      
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clearTimeout(retryTimeout);
      } else {
        retries++;
        if (retries <= maxRetries && Date.now() - startTime < timeoutLimit) {
          // Retry after 1000ms
          retryTimeout = setTimeout(attemptScroll, 1000);
        } else {
          console.error('Element not found within the specified retries or timeout limit.');
        }
      }
    };
  
    const startTime = Date.now();
    // Initial attempt after 1000ms
    retryTimeout = setTimeout(attemptScroll, 2500);
  }
  
  

  populate(
    componentConfig: OtherFundDetailsComponentConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ) {
    this.initialize(brConfig, component, page);
    const documents = component.getModels()?.documents;

    const doc = page.getContent(documents)?.getData<FundDocument>();
    this.productLabel = doc.productlabel;
    this.hasProductLabel = doc.productlabel?.length > 0;

    if (this.brConfig.fundId) {
      this.productLabel = this.sanitizer.bypassSecurityTrustHtml(// NOSONAR
        this.setRiskImages(this.insertAsOfDate(doc.productlabel))
      );// NOSONAR
    }
    this.cdr.detectChanges();
  }

  private insertAsOfDate(html: string): string {
    const start = html.indexOf('riskometer-img');
    if (start > -1) {
      const insertionPoint = start + html.substring(start).indexOf('<div class="text-meduim">');
      return (
        html.substring(0, insertionPoint) +
        '<h4 class="mb-3">' +
        this.translateService.instant('products.fund-other-details-riskometer') +
        '</h4>' +
        html.substring(insertionPoint)
      );
    }
    return html;
  }

  private setRiskImages(html: string): string {
    this.productLabelLoaded = true;
    // replaces in order so matching works properly
    return html
      .replace(
        /BM_RISK_LOW_MODERATE/g,
        this.translateService.instant('products.bm-risk-o-meter-low-moderate')
      )
      .replace(
        /BM_RISK_LOW/g,
        this.translateService.instant('products.bm-risk-o-meter-low')
      )
      .replace(
        /BM_RISK_MODERATELY_HIGH/g,
        this.translateService.instant('products.bm-risk-o-meter-moderately-high')
      )
      .replace(
        /BM_RISK_MODERATE/g,
        this.translateService.instant('products.bm-risk-o-meter-moderate')
      )
      .replace(
        /BM_RISK_HIGH/g,
        this.translateService.instant('products.bm-risk-o-meter-high')
      )
      .replace(
        /BM_RISK_VERY_HIGH/g,
        this.translateService.instant('products.bm-risk-o-meter-very-high')
      )
      .replace(
        /RISK_LOW_MODERATE/g,
        this.translateService.instant('products.risk-o-meter-low-moderate')
      )
      .replace(
        /RISK_LOW/g,
        this.translateService.instant('products.risk-o-meter-low')
      )
      .replace(
        /RISK_MODERATELY_HIGH/g,
        this.translateService.instant('products.risk-o-meter-moderately-high')
      )
      .replace(
        /RISK_MODERATE/g,
        this.translateService.instant('products.risk-o-meter-moderate')
      )
      .replace(
        /RISK_HIGH/g,
        this.translateService.instant('products.risk-o-meter-high')
      )
      .replace(
        /RISK_VERY_HIGH/g,
        this.translateService.instant('products.risk-o-meter-very-high')
      )
      .replace(
        /BM_RISK_LOW_MODERATE/g,
        this.translateService.instant('products.bm-risk-o-meter-low-moderate')
      )
      .replace(
        /BM_RISK_LOW/g,
        this.translateService.instant('products.bm-risk-o-meter-low')
      )
      .replace(
        /BM_RISK_MODERATELY_HIGH/g,
        this.translateService.instant('products.bm-risk-o-meter-moderately-high')
      )
      .replace(
        /BM_RISK_MODERATE/g,
        this.translateService.instant('products.bm-risk-o-meter-moderate')
      )
      .replace(
        /BM_RISK_HIGH/g,
        this.translateService.instant('products.bm-risk-o-meter-high')
      )
      .replace(
        /BM_RISK_VERY_HIGH/g,
        this.translateService.instant('products.bm-risk-o-meter-very-high')
      );
  }
}
